import React, { useEffect, useRef } from 'react';
import { BODY_GRID, PAGE_PAD } from '../layout';

// The client page skeleton: the SAME four zones in the SAME order for every
// client — header, attention strip, body (main column + right rail), Activity —
// so nothing reorders on data. Presentational: each zone is a slot.
//
// STICKY. HubShell scrolls its <main> below the 56px top bar, so `top-0` needs no
// offset. From `lg` the whole header (name row + facts row) sticks; below `lg` only
// the name row does — six facts would eat a phone. The header zone is therefore
// `display: contents` below `lg`, which lets the header's name row stick against the
// whole page instead of against its own short wrapper, and a real block from `lg`.
// Sticky rows are opaque and z-30, under the shell's z-50 drawers and overlays.

/**
 * Publish the height of whichever header element is currently sticky as
 * `--client-header-h` on the root, so anchors can clear it (layout.js ANCHOR_OFFSET).
 * The sticky element differs by breakpoint, so it is looked up by computed style
 * rather than by class. ResizeObserver only — no window resize listeners.
 */
function useStickyOffset(rootRef) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof ResizeObserver === 'undefined') return undefined;
    const targets = [...root.querySelectorAll('[data-sticky]')];
    const apply = () => {
      const active = targets.find((el) => getComputedStyle(el).position === 'sticky');
      root.style.setProperty('--client-header-h', `${active ? active.offsetHeight : 0}px`);
    };
    const observer = new ResizeObserver(apply);
    targets.forEach((el) => observer.observe(el));
    apply();
    return () => observer.disconnect();
  }, [rootRef]);
}

/**
 * @param {object} props
 * @param {React.ReactNode} props.header     The header rows (each brings its own page padding).
 * @param {React.ReactNode} props.strip      Attention strip.
 * @param {React.ReactNode} [props.pinned]   Pinned notes / page-level notices, under the strip.
 * @param {React.ReactNode} props.main       Left column of the body.
 * @param {React.ReactNode} props.rail       Right rail of the body (first on phones).
 * @param {React.ReactNode} props.activity   Full-width Activity card.
 * @param {React.ReactNode} [props.footer]   Elsewhere links.
 */
export default function ClientDetailLayout({ header, strip, pinned = null, main, rail, activity, footer = null }) {
  const rootRef = useRef(null);
  useStickyOffset(rootRef);

  return (
    <div ref={rootRef} data-testid="client-detail">
      <div data-zone="header" data-sticky className="contents lg:sticky lg:top-0 lg:z-30 lg:block">
        {header}
      </div>

      <div className={`${PAGE_PAD} py-5 space-y-5`}>
        <div data-zone="strip">{strip}</div>
        {pinned}
        {/* People first on phones: "who do I write to" is the question a person
            opening this page on a phone has, and Work → Requests → People puts it
            three cards deep. From lg the rail sits to the right as usual. */}
        <div data-zone="body" className={BODY_GRID}>
          <div className="order-2 min-w-0 space-y-5 lg:order-1">{main}</div>
          <div className="order-1 min-w-0 space-y-3 lg:order-2">{rail}</div>
        </div>
        <div data-zone="activity">{activity}</div>
        {footer}
      </div>
    </div>
  );
}
